import { copyTextToClipboard } from "@oysterhr/common"
import { Toast, useToast } from "@oysterhr/ui"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { SalaryCalculation } from "@oysterhr/schema"
import { booleanToTextValue } from "~/app/utils/helpers"
import { DotDropdown } from "@oysterhr/feature-components"
import { compact } from "lodash-es"
import { useCalculationsProvider } from "../../contexts"

interface CalculationActionsPopoverProps {
  calculation: SalaryCalculation
  /**
   * We need the index here because this is used to delete the calculation
   */
  index: number
}

const PUBLIC_ROUTE = "/cost-calculator"

export const CalculationActionsPopover = ({
  calculation,
  index,
}: CalculationActionsPopoverProps) => {
  const toast = useToast()
  const { t } = useTranslation(["CostCalculator", "components/ShareResult"])

  // Providers
  const { deleteCalculation, setIsEditMode } = useCalculationsProvider()

  async function shareQuoteHandler(e: React.MouseEvent) {
    e.stopPropagation()
    try {
      await copyTextToClipboard(shareableUrl)
      toast({
        render: (
          <Toast variation="success">
            <Toast.Content>{t("flashMessage.copyLink.success")}</Toast.Content>
          </Toast>
        ),
      })
    } catch {
      toast({
        render: (
          <Toast variation="error">
            <Toast.Content>{t("flashMessage.copyLink.error")}</Toast.Content>
          </Toast>
        ),
      })
    }
  }

  function deleteCalculationHandler(e: React.MouseEvent) {
    e.stopPropagation()
    deleteCalculation(index)
  }

  function editHandler(e: React.MouseEvent) {
    e.stopPropagation()
    setIsEditMode(true, index)
  }

  const dropdownValues = compact([
    { title: t("edit"), onClick: editHandler },
    { title: t("shareQuote"), onClick: shareQuoteHandler },
    { title: t("delete"), onClick: deleteCalculationHandler, danger: true },
  ])

  const shareableUrl = useMemo(() => {
    // Params for general calculation -- includes multiple calculations
    const queryParams = new URLSearchParams(document.location.search)

    // Selected currency for total hiring cost
    const totalCurrency = queryParams.get("totalCurrency")
    const cycle = queryParams.get("cycle")

    // Calculation data
    const countryCode = calculation.country.code
    const annualGrossSalary = calculation.annualGrossSalary
    const currencyCode = calculation.currency.code
    const stateOrProvince = calculation.stateOrProvince
    const vatServices = calculation.vat?.serviceWithinCountry
    const vatSales = calculation.vat?.salesWithinCountry
    const vatPercentages = calculation.vat?.salesPercentage

    // Params for shareable URL
    const shareParams = new URLSearchParams()
    shareParams.append("countryCode", encodeURIComponent(countryCode))
    shareParams.append("annualGrossSalary", encodeURIComponent(annualGrossSalary))
    shareParams.append("currencyCode", encodeURIComponent(currencyCode))
    if (stateOrProvince) {
      shareParams.append("stateOrProvince", encodeURIComponent(stateOrProvince))
    }
    if (totalCurrency) {
      shareParams.append("totalCurrency", encodeURIComponent(totalCurrency))
    }
    if (cycle) {
      shareParams.append("cycle", encodeURIComponent(cycle))
    }

    if (calculation.vat) {
      shareParams.append("vatServices", encodeURIComponent(booleanToTextValue(vatServices)))
      shareParams.append("vatSales", encodeURIComponent(booleanToTextValue(vatSales)))
      shareParams.append("vatPercentages", encodeURIComponent(vatPercentages ?? ""))
    }

    const url = `${document.location.origin + PUBLIC_ROUTE}?${shareParams.toString()}`

    return url
  }, [calculation])

  return <DotDropdown dropdownValues={dropdownValues} />
}
